import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import {
    get as loadIntegration,
    update as updateIntegration,
    sync as syncIntegration,
    push as pushIntegration,
    getIntegrationSchema,
} from '@/integrations/integration-sources/integration/integrations.utils';
import { http } from '@pendo/http';

/* eslint-disable id-length */

export const authStatuses = {
    OK: 'OK',
    PENDING: 'PENDING',
    NOT_STARTED: 'NOT_STARTED',
    ERROR: 'ERROR',
};

const STATUS_POLL_INTERVAL_MS = 1000;
const STATUS_POLL_REQUEST_TIMEOUT_MS = 10000;
const STATUS_POLL_MAX_RETRIES = 3;
const VALID_STATUS_POLL_RESPONSES = new Set(['DONE', 'ERROR', 'RUNNING', 'RUNNING_LOAD']);

export function prepareOutgoingSFIntegration (model) {
    model = cloneDeep(model);

    const joinKeys = model && model.data && model.data.joinKeys;
    if (joinKeys) {
        if (joinKeys.visitor) {
            joinKeys.contact = joinKeys.visitor;
            joinKeys.contact.kind = 'contact';
            delete joinKeys.visitor;
        }

        Object.entries(model.data.reports).forEach(([_, reportConfig]) => {
            if (reportConfig.joinedOn === 'visitor') {
                reportConfig.joinedOn = 'contact';
            }
            Object.entries(reportConfig.fields).forEach(([_, fieldConfig]) => {
                fieldConfig.table = joinKeys[reportConfig.joinedOn].sfTable;
            });
        });
    }

    return model;
}

export async function saveJoinKeys (integration, data) {
    if (data.reports) {
        Object.entries(data.reports).forEach(([id, config]) => {
            if (!config.fields || isEmpty(config.fields)) {
                delete data.reports[id];
            }
        });
    }

    const preparedIntegration = prepareOutgoingSFIntegration({ data });

    return updateIntegration({ type: integration.type, data: preparedIntegration.data });
}

export function prepareIncomingSFIntegration (model) {
    const clone = cloneDeep(model);

    if (clone && clone.data && clone.data.joinKeys) {
        const joins = Object.assign({}, clone.data.joinKeys);

        if (joins.contact) {
            joins.visitor = joins.contact;
            delete joins.contact;
        }

        Object.entries(joins).forEach(([key, joined]) => {
            joined.kind = key;
        });

        clone.data.joinKeys = joins;

        clone.syncInfo.forEach((syncInfo) => {
            if (syncInfo.kind) {
                syncInfo.kind = syncInfo.kind.replace(/contact$/, 'visitor');
            }

            if (!syncInfo.lastRunStatusMessage && syncInfo.lastRunStatusData) {
                syncInfo.lastRunStatusMessage = syncInfo.lastRunStatusData?.statusMessage;
            }
        });

        Object.entries(clone.data.reports).forEach(([_, reportConfig]) => {
            if (reportConfig.joinedOn === 'contact') {
                reportConfig.joinedOn = 'visitor';
            }

            Object.entries(reportConfig.fields).forEach(([_, fieldConfig]) => {
                fieldConfig.table = reportConfig.joinedOn;
            });
        });
    }

    return clone;
}

export async function getIntegration () {
    let sfIntegrationModel = await loadIntegration('salesforce');

    if (!sfIntegrationModel) {
        sfIntegrationModel = { authStatus: authStatuses.NOT_STARTED };
    }

    return prepareIncomingSFIntegration(sfIntegrationModel);
}

export async function loadPushSchema (kind) {
    const salesforce = await getIntegration();
    const config = find(salesforce.data.joinKeys, (config) => config.kind === kind);
    const results = await getIntegrationSchema({ type: 'salesforce', schema: config.sfTable, filter: 'push' });

    return Object.entries(results).map(([field, column]) => Object.assign(column, { field, kind }));
}

export async function syncNow (integration, kind) {
    if (kind === 'visitor') {
        kind = 'contact';
    }

    return syncIntegration({ type: integration.type, kind });
}

export async function pushNow (integration, kind) {
    if (kind === 'visitor') {
        kind = 'contact';
    }

    return pushIntegration({ type: integration.type, kind });
}

export async function onSyncStatus (...params) {
    await onStatus('sync', ...params);
}

export async function onPushStatus (...params) {
    await onStatus('push', ...params);
}

function retryPollStatus (action, kind, callback, statusUrl, retryCount) {
    if (retryCount < STATUS_POLL_MAX_RETRIES) {
        setTimeout(
            () => pollStatus(action, kind, callback, statusUrl, retryCount + 1),
            STATUS_POLL_INTERVAL_MS,
        );

        return;
    }

    callback({
        kind,
        pollingError: true,
        status: 'ERROR',
        statusMessage: 'An unknown error occurred.',
    });
}

async function onStatus (type, kind, resolveCB, errorCB, updateCB) {
    if (kind === 'visitor') {
        kind = 'contact';
    }

    await pollStatus(type, kind, (data) => {
        const { status, kind: responseKind } = data;

        if (kind !== responseKind) {
            return;
        }

        switch (status) {
            case 'ERROR':
                errorCB(data);
                break;
            case 'DONE':
                resolveCB(data);
                break;
            case 'RUNNING':
            case 'RUNNING_LOAD':
                updateCB(data);
                break;
        }
    });
}

export async function pollStatus (action, kind, callback, statusUrl, retryCount = 0) {
    const urlToGet = statusUrl || `/api/s/_SID_/integration/salesforce/status/${action}/${kind}`;
    let data;

    try {
        ({ data } = await http.get(urlToGet, { timeout: STATUS_POLL_REQUEST_TIMEOUT_MS }));
    } catch {
        retryPollStatus(action, kind, callback, statusUrl, retryCount);

        return;
    }

    if (!data || data.kind !== kind || !VALID_STATUS_POLL_RESPONSES.has(data.status)) {
        retryPollStatus(action, kind, callback, statusUrl, retryCount);

        return;
    }

    if (data.status === 'RUNNING' || data.status === 'RUNNING_LOAD') {
        setTimeout(() => pollStatus(action, kind, callback, statusUrl), STATUS_POLL_INTERVAL_MS);
    }

    data.statusMessage = data.statusMessage
        ? data.statusMessage[0].toUpperCase() + data.statusMessage.slice(1)
        : '';

    callback(data);
}

export function findSFTypeForJoinKeyType (sfIdSchema, sfField) {
    let sfObj = find(sfIdSchema, { key: sfField });

    sfObj = sfObj || {};

    return sfObj.Type || 'string';
}

export function prepareJoinKeysPayload ({ type, keys, reports, schema }) {
    Object.entries(keys).forEach((obj) => {
        // do not update the non-applicable level, however benign that might be
        // ^ I don't know what this means, I'm just moving this to Vue
        if (obj[0] !== type) return;
        keys[obj[0]].sfType = findSFTypeForJoinKeyType(schema, keys[obj[0]].sf);
    });

    if (keys.visitor) {
        keys.contact = keys.visitor;
        keys.contact.kind = 'contact';
        delete keys.visitor;
    }

    if (reports) {
        Object.keys(reports).forEach((reportConfig) => {
            const report = reports[reportConfig];
            if (!report) return;

            if (report.joinedOn === 'visitor') {
                report.joinedOn = 'contact';
            }

            Object.keys(report.fields).forEach((key) => {
                const fieldConfig = report.fields[key];
                fieldConfig.table = keys[report.joinedOn].sfTable;
            });
        });
    }

    const payload = {
        [type]: { keys, reports },
    };

    return payload;
}
